import { useEffect } from "react";
import { DeepPartial, FieldValues } from "react-hook-form";
import { useAavoForm } from "src/components/common/forms/useAavoForm/useAavoForm.ts";
import { UseAavoFormParams, UseAavoFormReturn } from "src/components/common/forms/useAavoForm/types.ts";
import { logError } from "src/errorHandling/errorLogging.ts";
import { useAavoWizardValidation } from "src/components/common/wizard/AavoWizardContext.ts";

interface UseAavoWizardStepFormParams<TFieldValues extends FieldValues>
	extends Omit<UseAavoFormParams<TFieldValues, void>, "onSubmit"> {
	onUpdate: (values: DeepPartial<TFieldValues>) => void;
}

export function useAavoWizardStepForm<TFieldValues extends FieldValues>({
	onUpdate,
	...useAavoFormParams
}: UseAavoWizardStepFormParams<TFieldValues>): UseAavoFormReturn<TFieldValues, void> {
	const { onValidationChange, onFormEdited } = useAavoWizardValidation();

	const useAavoFormReturn = useAavoForm<TFieldValues, void>({
		...useAavoFormParams,
		onSubmit: () => {},
	});
	const { watch, formState, trigger } = useAavoFormReturn;

	useEffect(() => {
		const subscription = watch((newValues) => {
			onUpdate(newValues);
			onFormEdited?.();
		});
		return () => subscription.unsubscribe();
	}, [watch, onUpdate, onFormEdited]);

	useEffect(() => {
		onValidationChange(formState.isValid);
	}, [formState.isValid, onValidationChange]);

	useEffect(() => {
		trigger().catch(logError);
	}, [trigger]);

	return useAavoFormReturn;
}
